import { useCallback, useEffect, useMemo, useState } from "react";
import { listarMatriculas } from "../../../../services/matriculasService";
import { listarCursos } from "../../../../services/cursosService";
import { normalizeMatricula, nombreCompleto as nombreEstudianteMat } from "../../matriculas/adapters/matriculasAdapter";
import { showToast } from "../../../../utils/showToast";

const buildMatriculaLabel = (normalized = {}) => {
  const estudiante = nombreEstudianteMat(normalized);
  const promo = normalized.promocion || "Sin promocion";
  const codigo = normalized.codigoMatricula || `#${normalized.id}`;
  return `Codigo ${codigo} - ${estudiante} - ${promo}`;
};

const buildPromocionesOptions = (matriculas = []) => {
  const byId = new Map();
  matriculas.forEach((item) => {
    const promocionId = item?.promocionId;
    if (promocionId === undefined || promocionId === null || promocionId === "") return;
    const nombre = (item?.promocion || "").trim() || `Promocion #${promocionId}`;
    byId.set(String(promocionId), { value: String(promocionId), label: nombre });
  });
  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label, "es", { sensitivity: "base" }));
};

export default function useFormacionesOptions() {
  const [matriculasOptions, setMatriculasOptions] = useState([]);
  const [cursosOptions, setCursosOptions] = useState([]);
  const [promocionesOptions, setPromocionesOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const [data, cursosData] = await Promise.all([
        listarMatriculas(),
        listarCursos(),
      ]);

      const dataArr = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      const matriculasNormalized = dataArr.map((item) => normalizeMatricula(item));

      const options = matriculasNormalized.map((normalized) => ({
        value: normalized.id,
        label: buildMatriculaLabel(normalized),
        codigoMatricula: normalized.codigoMatricula || "",
        estudianteId: normalized.estudianteId,
        estudianteNombre: nombreEstudianteMat(normalized),
        documento: normalized.documentoIdentidad || "",
        promocionId: normalized.promocionId ?? null,
        promocionNombre: normalized.promocion || "",
      }));
      setMatriculasOptions(options);
      setPromocionesOptions(buildPromocionesOptions(matriculasNormalized));

      // Creacion manual (sin matricula) usa el nivel_id del curso elegido directamente, sin
      // pasar por ninguna matricula ni por el COALESCE del subtipo. Un curso "padre" que tiene
      // variantes comerciales (ej. REENTRENAMIENTO) normalmente no tiene nivel_id propio, asi que
      // seleccionarlo siempre fallaria la validacion — se oculta y se obliga a elegir la variante
      // real (ej. REENTRENAMIENTO N1/N2), que si tiene nivel_id.
      const cursosArr = Array.isArray(cursosData) ? cursosData : [];
      const idsConVariantes = new Set(
        cursosArr.map((c) => c.curso_oficial_id ?? c.cursoOficialId).filter(Boolean)
      );
      const cursoOpts = cursosArr
        .filter((c) => !idsConVariantes.has(c.id))
        .map((c) => ({
          value: c.id,
          label: c.nombre,
          nivel_id: c.nivel_id ?? c.nivelId,
          es_reentrenamiento: c.es_reentrenamiento ?? c.esReentrenamiento,
        }));
      setCursosOptions(cursoOpts);
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudieron cargar opciones." });
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  return useMemo(
    () => ({
      matriculasOptions,
      cursosOptions,
      promocionesOptions,
      loadingOptions,
      reloadOptions: loadOptions,
    }),
    [matriculasOptions, cursosOptions, promocionesOptions, loadingOptions, loadOptions]
  );
}
